import { EduActivity, EduActivityType, EduAdminDiscipline, EduAnswerValue, EduAttemptQuestion, EduQuestionDefinition, EduQuestionType } from './types';

export const EDU_ACTIVITY_LABELS: Record<EduActivityType, string> = {
    resource: 'Material', folder: 'Pasta de materiais', url: 'Link', page: 'Página', label: 'Orientação',
    assign: 'Tarefa', forum: 'Fórum pedagógico', quiz: 'Questionário',
};
export const EDU_QUESTION_LABELS: Record<EduQuestionType, string> = {
    multichoice: 'Múltipla escolha', truefalse: 'Verdadeiro ou falso', shortanswer: 'Resposta curta',
    numerical: 'Resposta numérica', match: 'Associação', gapselect: 'Lacunas com opções',
    ddwtos: 'Lacunas com palavras', ddimageortext: 'Associação em imagem', essay: 'Dissertativa',
    description: 'Texto informativo', random: 'Seleção aleatória de questões',
};

export function defaultQuestionDefinition(type: EduQuestionType): EduQuestionDefinition {
    switch (type) {
        case 'multichoice': return { multiple: false, shuffle: true, options: [
            { id: 'a', textHtml: '', fraction: 1 }, { id: 'b', textHtml: '', fraction: 0 },
        ] };
        case 'truefalse': return { answer: true, trueFeedbackHtml: '', falseFeedbackHtml: '' };
        case 'shortanswer': return { caseSensitive: false, allowWildcards: false, answers: [{ text: '', fraction: 1 }] };
        case 'numerical': return { answers: [{ value: 0, tolerance: 0, fraction: 1 }], unitRequired: false };
        case 'match': return { pairs: [{ id: 'p1', promptHtml: '', answerHtml: '' }, { id: 'p2', promptHtml: '', answerHtml: '' }], shuffle: true };
        case 'gapselect':
        case 'ddwtos': return { textHtml: '[[gap:g1]]', gaps: [{ id: 'g1', group: 1, answerOptionId: 'o1' }],
            options: [{ id: 'o1', textHtml: '', group: 1, reusable: false }] };
        case 'ddimageortext': return { backgroundFileId: 0, width: 800, height: 600,
            items: [{ id: 'i1', textHtml: '', reusable: false }],
            zones: [{ id: 'z1', x: 0, y: 0, width: 100, height: 100, answerItemId: 'i1' }] };
        case 'essay': return { allowText: true, allowFiles: false, gradingInstructionsHtml: '', sampleAnswerHtml: '' };
        case 'description': return { textHtml: '' };
        case 'random': return { poolQuestionIds: [], includeSubcategories: false };
    }
}

export function getEduActivities(discipline: EduAdminDiscipline): EduActivity[] {
    const items = new Map<number, EduActivity>();
    for (const section of discipline.sections || []) for (const item of section.activities || []) items.set(item.id, item);
    for (const item of discipline.activities || []) items.set(item.id, item);
    return Array.from(items.values()).sort((a, b) => a.position - b.position || a.id - b.id);
}

export function parseOptionalNumber(value: string, field: string, minimum = 0): number | null {
    if (!value.trim()) return null;
    const result = Number(value.replace(',', '.'));
    if (!Number.isFinite(result) || result < minimum) throw new Error(`${field} deve ser um número maior ou igual a ${minimum}.`);
    return result;
}

export function formatEduDate(value: string | null | undefined): string {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('pt-BR');
}

export function formatEduFileSize(size: number): string {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} KiB`;
    return `${(size / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MiB`;
}

export const EDU_ACCESS_LABELS: Record<string, string> = {
    edu_open: 'Entrada no Fermi Edu', discipline_open: 'Abertura de disciplina',
    activity_open: 'Abertura de atividade', file_open: 'Abertura de material',
    submission_created: 'Envio de tarefa', forum_posted: 'Resposta em fórum',
    quiz_started: 'Início de questionário', quiz_finished: 'Finalização de questionário',
};

export function eduPlainText(html: unknown): string {
    const value = typeof html === 'string' ? html : '';
    return typeof DOMParser === 'undefined' ? value.replace(/<[^>]*>/g, '') : new DOMParser().parseFromString(value, 'text/html').body.textContent || '';
}
export function formatEduAnswer(question: EduAttemptQuestion, value: EduAnswerValue | undefined): string {
    if (value == null) return question.type === 'description' ? 'Texto informativo, sem resposta.' : 'Não respondida';
    const d = question.publicDefinition;
    const rows = (key: string) => (Array.isArray(d[key]) ? d[key] : []) as Record<string, unknown>[];
    const label = (key: string, id: string, textKey: string) => eduPlainText(rows(key).find(row => row.id === id)?.[textKey]) || 'Opção indisponível';
    if (question.type === 'multichoice' && Array.isArray(value)) return (value as string[]).map(id => label('options', id, 'textHtml')).join('; ') || 'Não respondida';
    if (question.type === 'truefalse' && typeof value === 'boolean') return value ? 'Verdadeiro' : 'Falso';
    if (typeof value === 'string') return value;
    if (question.type === 'numerical' && !Array.isArray(value) && typeof value === 'object' && 'value' in value) return `${value.value}${value.unit ? ` ${value.unit}` : ''}`;
    if (question.type === 'match' && Array.isArray(value)) return (value as { pairId: string; answerId: string }[]).map(row => `${label('pairs', row.pairId, 'promptHtml')} → ${label('answers', row.answerId, 'textHtml')}`).join('; ');
    if (['gapselect', 'ddwtos'].includes(question.type) && Array.isArray(value)) return (value as { gapId: string; optionId: string }[]).map(row => `Lacuna ${rows('gaps').findIndex(gap => gap.id === row.gapId) + 1}: ${label('options', row.optionId, 'textHtml')}`).join('; ');
    if (question.type === 'ddimageortext' && Array.isArray(value)) return (value as { zoneId: string; itemId: string }[]).map(row => `Zona ${rows('zones').findIndex(zone => zone.id === row.zoneId) + 1}: ${label('items', row.itemId, 'textHtml')}`).join('; ');
    if (question.type === 'essay' && !Array.isArray(value) && typeof value === 'object' && 'text' in value) return value.text;
    return 'Resposta disponível para revisão.';
}
